import { i18nFunct } from '@lucid/gatsby-theme-lucid';
import { graphql, useStaticQuery } from 'gatsby';
import React, { useContext } from 'react';

import LayoutContext from '../../contexts/layoutContext';
import { Drupal_MenuLinkContentMoreExamplesList } from '../../generated/graphql';
import Link from '../link/link';
import './more-examples.scss';

interface Subitem {
  text: string;
  link: string;
}

interface Item {
  uuid?: string;
  title?: string;
  link?: string;
  img?: string;
  items: Subitem[];
}

interface MoreExamplesProps {
  links: Drupal_MenuLinkContentMoreExamplesList[];
  showLinks?: boolean;
  noPadding?: boolean;
}

const MoreExamples = ({ showLinks, links, noPadding }: MoreExamplesProps) => {
  const list = links.reduce((obj: Item[], item) => {
    const path = item.fieldTranslatedLink
      ? item.fieldTranslatedLink
      : item.link!.url!.path;
    if (!item.parent) {
      obj.push({
        uuid: item.entityUuid || '',
        title: item.title || '',
        link: path || '',
        img: item.fieldCategoryImage || '',
        items: []
      });
    } else {
      const parentUuid = item.parent.replace('menu_link_content:', '');
      const itemParent = obj.find(parent => parent.uuid === parentUuid);
      if (itemParent) {
        itemParent.items.push({
          text: item.title || '',
          link: path || ''
        });
      }
    }
    return obj;
  }, []);

  const { i18nData } = useStaticQuery(
    graphql`
      {
        i18nData: allFile(filter: { sourceInstanceName: { eq: "i18n" } }) {
          edges {
            node {
              name
              childSrcJson {
                mktg_more_examples_header
              }
            }
          }
        }
      }
    `
  );

  const {
    language: { i18nKey }
  } = useContext(LayoutContext);

  const i18n = i18nFunct(i18nData, i18nKey);

  return (
    <aside className="more-examples">
      <section
        className={`lucid-container lucid-container--flex examples-container ${
          noPadding ? 'examples-container--no-h-padding' : ''
        }`}
      >
        <h2 className="more-examples__header">
          {i18n('mktg-more-examples-header')}
        </h2>
        {list.map((section, i) => (
          <div key={section.title} className="examples-column">
            {showLinks ? (
              <Link className="more-examples__link" to={section.link!}>
                <img src={section.img} alt={section.title} data-has-retina />
              </Link>
            ) : (
              <span>
                <img
                  className="more-examples__image"
                  src={section.img}
                  alt={section.title}
                  data-has-retina
                />
              </span>
            )}

            {showLinks ? (
              <h3 className="column__header">
                <Link
                  className={`column__link column__link--${i}`}
                  to={section.link!}
                >
                  {section.title}
                </Link>
              </h3>
            ) : (
              <h3 className="column__header">{section.title}</h3>
            )}

            <ul className="column-list">
              {section.items.map(item =>
                showLinks ? (
                  <li className="column-list__item" key={item.text}>
                    <Link
                      className={`more-examples__link more-examples__link--${i}`}
                      to={item.link}
                    >
                      {item.text}
                    </Link>
                  </li>
                ) : (
                  <li className="more-examples__link" key={item.text}>
                    {item.text}
                  </li>
                )
              )}
            </ul>
          </div>
        ))}
      </section>
    </aside>
  );
};

export default MoreExamples;
